import { Box, Vec, type VecLike } from "../geometry";
import {
  distanceToSegment,
  nearestOnSegment,
  pointInPolygon,
  segmentsIntersect,
} from "./math";

// Shape geometry in the shape's local space, used for bounds, hit testing and
// marquee selection. Constructor options match what Planroom's shapes pass.
export type Geometry2dOptions = {
  isFilled?: boolean;
  isClosed?: boolean;
  isLabel?: boolean;
  excludeFromShapeBounds?: boolean;
};

export abstract class Geometry2d {
  isFilled: boolean;
  isClosed: boolean;
  isLabel: boolean;
  excludeFromShapeBounds: boolean;
  private cachedVertices?: Vec[];
  private cachedBounds?: Box;

  constructor(opts: Geometry2dOptions = {}) {
    this.isFilled = opts.isFilled ?? false;
    this.isClosed = opts.isClosed ?? true;
    this.isLabel = opts.isLabel ?? false;
    this.excludeFromShapeBounds = opts.excludeFromShapeBounds ?? false;
  }

  abstract getVertices(): Vec[];
  get vertices() {
    return (this.cachedVertices ??= this.getVertices());
  }
  /** Vertices that count towards the shape's bounds. */
  getBoundsVertices(): Vec[] {
    return this.excludeFromShapeBounds ? [] : this.vertices;
  }
  get bounds() {
    return (this.cachedBounds ??= Box.FromPoints(this.getBoundsVertices()));
  }
  get center() {
    return this.bounds.center;
  }

  /** Line segments of the outline, closing the loop when closed. */
  segments(): [Vec, Vec][] {
    const v = this.vertices;
    const out: [Vec, Vec][] = [];
    for (let i = 0; i < v.length - 1; i++) out.push([v[i], v[i + 1]]);
    if (this.isClosed && v.length > 2) out.push([v[v.length - 1], v[0]]);
    return out;
  }

  nearestPoint(point: VecLike): Vec {
    let best = this.vertices[0] ?? new Vec(),
      bestDist = Infinity;
    for (const [a, b] of this.segments()) {
      const p = nearestOnSegment(point, a, b),
        d = Vec.Dist(point, p);
      if (d < bestDist) {
        best = p;
        bestDist = d;
      }
    }
    if (!this.segments().length && this.vertices.length === 1)
      return this.vertices[0];
    return best;
  }

  /** Distance to the outline; negative inside a filled, closed shape. */
  distanceToPoint(point: VecLike, hitInside = false) {
    let distance = Infinity;
    for (const [a, b] of this.segments())
      distance = Math.min(distance, distanceToSegment(point, a, b));
    if (this.vertices.length === 1)
      distance = Vec.Dist(point, this.vertices[0]);
    if (
      this.isClosed &&
      (this.isFilled || hitInside) &&
      pointInPolygon(point, this.vertices)
    )
      return -distance;
    return distance;
  }

  hitTestPoint(point: VecLike, margin = 0, hitInside = false) {
    return this.distanceToPoint(point, hitInside) <= margin;
  }

  /** Whether the outline touches or lies inside `polygon` (marquee selection). */
  overlapsPolygon(polygon: VecLike[]) {
    if (this.vertices.some((v) => pointInPolygon(v, polygon))) return true;
    if (this.isClosed && polygon.some((p) => pointInPolygon(p, this.vertices)))
      return this.isFilled;
    const edges: [VecLike, VecLike][] = polygon.map((p, i) => [
      p,
      polygon[(i + 1) % polygon.length],
    ]);
    return this.segments().some(([a, b]) =>
      edges.some(([c, d]) => segmentsIntersect(a, b, c, d)),
    );
  }

  toSvgPath() {
    const v = this.vertices;
    if (!v.length) return "";
    return `M${v.map((p) => `${p.x},${p.y}`).join("L")}${this.isClosed ? "Z" : ""}`;
  }
}

export class Polyline2d extends Geometry2d {
  readonly points: Vec[];
  constructor(opts: Geometry2dOptions & { points: VecLike[] }) {
    super({ ...opts, isClosed: false });
    this.points = opts.points.map((p) => Vec.From(p));
  }
  getVertices() {
    return this.points;
  }
}

export class Polygon2d extends Geometry2d {
  readonly points: Vec[];
  constructor(opts: Geometry2dOptions & { points: VecLike[] }) {
    super({ ...opts, isClosed: true });
    this.points = opts.points.map((p) => Vec.From(p));
  }
  getVertices() {
    return this.points;
  }
}

export class Rectangle2d extends Polygon2d {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  constructor(
    opts: Geometry2dOptions & {
      x?: number;
      y?: number;
      width: number;
      height: number;
    },
  ) {
    const x = opts.x ?? 0,
      y = opts.y ?? 0;
    super({
      ...opts,
      points: [
        { x, y },
        { x: x + opts.width, y },
        { x: x + opts.width, y: y + opts.height },
        { x, y: y + opts.height },
      ],
    });
    this.x = x;
    this.y = y;
    this.w = opts.width;
    this.h = opts.height;
  }
}

/** A circle given by its bounding box origin and radius (approximated by a polygon). */
export class Circle2d extends Geometry2d {
  readonly x: number;
  readonly y: number;
  readonly radius: number;
  constructor(
    opts: Geometry2dOptions & { x?: number; y?: number; radius: number },
  ) {
    super({ ...opts, isClosed: true });
    this.x = opts.x ?? 0;
    this.y = opts.y ?? 0;
    this.radius = opts.radius;
  }
  getVertices() {
    const n = Math.max(16, Math.min(128, Math.round(this.radius / 2))),
      cx = this.x + this.radius,
      cy = this.y + this.radius;
    return Array.from(
      { length: n },
      (_, i) =>
        new Vec(
          cx + Math.cos((i / n) * Math.PI * 2) * this.radius,
          cy + Math.sin((i / n) * Math.PI * 2) * this.radius,
        ),
    );
  }
  override distanceToPoint(point: VecLike, hitInside = false) {
    const d = Vec.Dist(point, {
      x: this.x + this.radius,
      y: this.y + this.radius,
    });
    const edge = Math.abs(d - this.radius);
    return d < this.radius && (this.isFilled || hitInside) ? -edge : edge;
  }
}

export class Ellipse2d extends Geometry2d {
  constructor(
    readonly opts: Geometry2dOptions & { width: number; height: number },
  ) {
    super({ ...opts, isClosed: true });
  }
  getVertices() {
    const { width: w, height: h } = this.opts;
    const n = 64;
    return Array.from(
      { length: n },
      (_, i) =>
        new Vec(
          w / 2 + (Math.cos((i / n) * Math.PI * 2) * w) / 2,
          h / 2 + (Math.sin((i / n) * Math.PI * 2) * h) / 2,
        ),
    );
  }
}

export class Group2d extends Geometry2d {
  readonly children: Geometry2d[];
  constructor(opts: Geometry2dOptions & { children: Geometry2d[] }) {
    super({ ...opts, isClosed: true });
    this.children = opts.children;
  }
  getVertices() {
    return this.children.filter((c) => !c.isLabel).flatMap((c) => c.vertices);
  }
  override getBoundsVertices() {
    return this.children.flatMap((c) => c.getBoundsVertices());
  }
  override distanceToPoint(point: VecLike, hitInside = false) {
    return Math.min(
      ...this.children.map((c) => c.distanceToPoint(point, hitInside)),
    );
  }
  override nearestPoint(point: VecLike) {
    let best = new Vec(),
      bestDist = Infinity;
    for (const child of this.children) {
      const p = child.nearestPoint(point),
        d = Vec.Dist(p, point);
      if (d < bestDist) {
        best = p;
        bestDist = d;
      }
    }
    return best;
  }
  override hitTestPoint(point: VecLike, margin = 0, hitInside = false) {
    return this.children.some((c) => c.hitTestPoint(point, margin, hitInside));
  }
  override overlapsPolygon(polygon: VecLike[]) {
    return this.children.some((c) => !c.isLabel && c.overlapsPolygon(polygon));
  }
  override toSvgPath() {
    return this.children.map((c) => c.toSvgPath()).join("");
  }
}
